import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Bed, Bath, Square, MapPin, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { FEATURED_LISTINGS } from '../data/mockData';

const FavoritesPage = () => {
  const { user, favorites, removeFavorite } = useAuth();

  const favoriteListings = FEATURED_LISTINGS.filter(listing => favorites.includes(listing.id));

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Heart size={48} className="text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view your favorites</h1>
          <p className="text-gray-500 mb-6">Save properties you love and access them anytime</p>
          <Link to="/login">
            <Button className="bg-red-600 hover:bg-red-700">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Favorites</h1>
          <p className="text-gray-600">{favoriteListings.length} saved properties</p>
        </div>

        {favoriteListings.length === 0 ? (
          <div className="text-center py-16">
            <Heart size={48} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No favorites yet</h2>
            <p className="text-gray-500 mb-6">Start browsing and save properties you love</p>
            <Link to="/search">
              <Button className="bg-red-600 hover:bg-red-700">Browse Properties</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favoriteListings.map((property) => (
              <Card key={property.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                <Link to={`/property/${property.id}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={property.image} 
                      alt={property.address}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-4 left-4">
                      <div className="text-white text-2xl font-bold drop-shadow-lg">
                        ${property.price.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-red-600 mt-1 flex-shrink-0" />
                      <div>
                        <Link to={`/property/${property.id}`}>
                          <h3 className="font-semibold text-gray-900 hover:text-red-600 transition-colors">
                            {property.address}
                          </h3>
                        </Link>
                        <p className="text-gray-500 text-sm">
                          {property.city}, {property.province}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFavorite(property.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Remove from favorites"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="flex items-center gap-4 text-gray-600 text-sm">
                    <div className="flex items-center gap-1">
                      <Bed size={16} />
                      <span>{property.beds} Beds</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath size={16} />
                      <span>{property.baths} Baths</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Square size={16} />
                      <span>{property.sqft.toLocaleString()} sqft</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
